import React from 'react';
import styled from 'styled-components';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { date, object, string } from 'yup';
import type { ApplicantInformation } from '../graphql';
import { PAGE_TEXT, ApplicantInfoFormContent } from '../../Signup/components';
import { Button, ButtonContainer } from '../../../components';
import { handleReadySubmit, VIEW_MODE } from '../../../utils';

type UserInformationContentProps = {
  initialValues: ApplicantInformation;
  handleUpdateApplicant: (applicant: ApplicantInformation) => void;
  viewMode: VIEW_MODE;
};

export const UserInformationForm: React.FunctionComponent<
  UserInformationContentProps
> = (props: UserInformationContentProps) => {
  const { initialValues, handleUpdateApplicant, viewMode } = props;
  const navigate = useNavigate();

  const formik = useFormik<ApplicantInformation>({
    initialValues,
    validationSchema: object({
      surname: string()
        .required(PAGE_TEXT.REQUIRED)
        // [RPG-1924] only English letters (upper/lower case) and spaces allowed
        // prohibits accented characters (à, é, ü, etc.)
        .matches(/^[A-Za-z\s]+$/, 'English letters only')
        .test(
          'byte-length',
          'Exceeds maximum length of 30 characters',
          value => {
            return value.length <= 30;
          },
        ),
      givenName: string()
        .required(PAGE_TEXT.REQUIRED)
        .matches(/^[A-Za-z\s]+$/, 'English letters only')
        .test(
          'byte-length',
          'Exceeds maximum length of 30 characters',
          value => {
            return value?.length <= 30;
          },
        ),
      chineseName: string()
        // [RPG-1924] strict Chinese character validation (simplified/traditional)
        // \u4e00-\u9fff covers most Chinese characters (including common extensions)
        .matches(/^[\u4e00-\u9fff]*$/, PAGE_TEXT.CHINESE_NAME_REQUIRED)
        // prevent mixed Chinese-English names
        .test('no-mixed', PAGE_TEXT.CHINESE_NAME_REQUIRED, value => {
          return !/[A-Za-z]/.test(value ?? '');
        })
        .test('maxlength', 'Exceeds maximum length of 10 characters', value => {
          return (value ?? '')?.length <= 10;
        }),
      dateOfBirth: date().required(PAGE_TEXT.REQUIRED),
      gender: string().required(PAGE_TEXT.REQUIRED),
      identityType: string().required(PAGE_TEXT.REQUIRED),
      identityDocumentNumber: string().required(PAGE_TEXT.REQUIRED),
      identityDocumentType: string().required(PAGE_TEXT.REQUIRED),
      nationality: string().required(PAGE_TEXT.REQUIRED),
      province: string().when('nationality', ([nationality], schema) => {
        const isChina: boolean = nationality === 'CHN';
        return isChina ? schema.required('Required') : schema.notRequired();
      }),
      addressCountryRegion: string().required(PAGE_TEXT.REQUIRED),
      addressLine1: string()
        .required(PAGE_TEXT.REQUIRED)
        .test(
          'byte-length',
          'Exceeds maximum length of 32 characters',
          value => {
            return value.length <= 32;
          },
        ),
      addressLine2: string()
        .nullable()
        .test(
          'byte-length',
          'Exceeds maximum length of 32 characters',
          value => {
            return String(value).length <= 32;
          },
        ),
      addressLine3: string()
        .nullable()
        .test(
          'byte-length',
          'Exceeds maximum length of 32 characters',
          value => {
            return String(value).length <= 32;
          },
        ),
      mobileNumber: string()
        .min(5, PAGE_TEXT.REQUIRED)
        .required(PAGE_TEXT.REQUIRED),
      disabilitySpecialType: string().required(PAGE_TEXT.REQUIRED),
      disabilitySpecialDetail: string().when(
        'disabilitySpecialType',
        ([disabilitySpecialType], schema) => {
          const detailsRequired: boolean =
            Boolean(disabilitySpecialType) && disabilitySpecialType !== 'NA';
          return detailsRequired
            ? schema.required(PAGE_TEXT.REQUIRED)
            : schema.notRequired();
        },
      ),
    }),
    onSubmit: handleUpdateApplicant,
  });

  const handleBack = (): void => {
    navigate(-1);
  };

  const buttonMapping: Partial<Record<VIEW_MODE, React.ReactNode>> = {
    [VIEW_MODE.SIGN_UP]: (
      <>
        <Button variant="text" onClick={handleBack}>
          {PAGE_TEXT.BACK}
        </Button>
        <Button variant="contained" type="submit">
          {PAGE_TEXT.SUBMIT}
        </Button>
      </>
    ),
    [VIEW_MODE.VIEW_AS_APPLICANT]: (
      <>
        <Button variant="text" onClick={handleBack}>
          {PAGE_TEXT.BACK}
        </Button>
        <Button variant="contained" type="submit">
          {PAGE_TEXT.SAVE}
        </Button>
      </>
    ),
    [VIEW_MODE.VIEW_AS_GS_MEMBER]: (
      <>
        <Button variant="text" onClick={handleBack}>
          {PAGE_TEXT.BACK}
        </Button>
        <Button variant="contained" type="submit">
          {PAGE_TEXT.UPDATE}
        </Button>
      </>
    ),
    [VIEW_MODE.VIEW_AS_AR_MEMBER]: (
      <>
        <Button variant="text" onClick={handleBack}>
          {PAGE_TEXT.BACK}
        </Button>
        <Button variant="contained" type="submit">
          {PAGE_TEXT.UPDATE}
        </Button>
      </>
    ),
    [VIEW_MODE.SUPER_ADMIN]: (
      <Button variant="contained" type="submit">
        {PAGE_TEXT.UPDATE}
      </Button>
    ),
    [VIEW_MODE.OTHER]: (
      <Button variant="text" onClick={handleBack}>
        {PAGE_TEXT.BACK}
      </Button>
    ),
  };

  return (
    <Container>
      <form noValidate onSubmit={handleReadySubmit(formik)}>
        <ApplicantInfoFormContent formik={formik} viewMode={viewMode} />
        <ButtonContainer spacing={4}>{buttonMapping[viewMode]}</ButtonContainer>
      </form>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 3rem;

  form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;

    width: calc(100%);
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }

  button {
    margin: 2rem;
  }
`;
